import React from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Card, Row, Col, Button, Typography, Space, Descriptions, Spin, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, BarChartOutlined } from "@ant-design/icons";
import type { DescriptionsProps } from 'antd';

const { Title } = Typography;



export const FiscalisationDashboard: React.FC = () => {

  // Get device status
  const { data, isLoading, refetch } = useCustom({
    url: "fiscalisation/status",
    method: "get",
  });

  // Open day mutation
  const { mutate: openDay, isLoading: openDayLoading } = useCustomMutation();

  // Close day mutation
  const { mutate: closeDay, isLoading: closeDayLoading } = useCustomMutation();

  const deviceStatus = data?.data;

  // Format date
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (date.getFullYear() <= 1) return '-';
      return date.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch (e) {
      return '-';
    }
  };

  // Handle open day
  const handleOpenDay = () => {
    openDay({
      url: "fiscalisation/open",
      method: "post",
      successNotification: {
        message: "Success",
        description: "Fiscal day opened successfully",
        type: "success",
      },
      errorNotification: {
        message: "Error",
        description: "Failed to open fiscal day",
        type: "error",
      },
    }, {
      onSuccess: () => {
        refetch(); // Refresh status after successful open day
      }
    });
  };

  // Handle close day
  const handleCloseDay = () => {
    closeDay({
      url: "fiscalisation/close",
      method: "post",
      successNotification: {
        message: "Success",
        description: "Fiscal day closed successfully",
        type: "success",
      },
      errorNotification: {
        message: "Error",
        description: "Failed to close fiscal day",
        type: "error",
      },
    }, {
      onSuccess: () => {
        refetch(); // Refresh status after successful close day
      },
    });
  };

  // Status information items
  const statusItems: DescriptionsProps['items'] = [
    {
      key: '1',
      label: 'Device Connection',
      children: deviceStatus?.deviceID ?
        <Tag color="green">Connected</Tag> :
        <Tag color="red">Disconnected</Tag>
    },
    {
      key: '2',
      label: 'Fiscal Day Status',
      children: (() => {
        if (deviceStatus?.fiscalDayStatus === "OPENED" || deviceStatus?.fiscalDayStatus === "FiscalDayOpened") {
          return <Tag color="blue">Open</Tag>;
        } else if (deviceStatus?.fiscalDayStatus === "FiscalDayCloseFailed") {
          return <Tag color="red">Close Failed</Tag>;
        } else {
          return <Tag color="default">Closed</Tag>;
        }
      })()
    },
    {
      key: '3',
      label: 'Timestamp',
      children: formatDate(deviceStatus?.timestamp)
    },
    {
      key: '4',
      label: 'Last Fiscal Day No',
      children: deviceStatus?.lastFiscalDayNo || '-'
    },
    {
      key: '5',
      label: 'Device ID',
      children: deviceStatus?.deviceID || '-'
    },
    {
      key: '6',
      label: 'Last Receipt Global No',
      children: deviceStatus?.lastReceiptGlobalNo || '-'
    },
  ];

  return (
    <div>
      <Title level={4}>Fiscal Device Management</Title>

      <Row gutter={[16, 16]}>
        <Col span="24">
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleOpenDay}
                  loading={openDayLoading}
                  disabled={(deviceStatus?.fiscalDayStatus === "OPENED" || deviceStatus?.fiscalDayStatus === "FiscalDayOpened" || !deviceStatus?.deviceID) && deviceStatus?.fiscalDayStatus !== "FiscalDayCloseFailed"}
                >
                  Open Fiscal Day
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={handleCloseDay}
                  loading={closeDayLoading}
                  disabled={(deviceStatus?.fiscalDayStatus !== "OPENED" && deviceStatus?.fiscalDayStatus !== "FiscalDayCloseFailed" && deviceStatus?.fiscalDayStatus !== "FiscalDayOpened") || !deviceStatus?.deviceID}
                >
                  Close Fiscal Day
                </Button>
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => refetch()}
                  loading={isLoading}
                >
                  Refresh Status
                </Button>
              </Space>

              <div style={{ marginTop: 20 }}>
                {isLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16 }}>Loading device status...</div>
                  </div>
                ) : (
                  <Descriptions
                    title="Fiscal Device Status"
                    bordered
                    items={statusItems}
                    column={3}
                  />
                )}
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
